import { createRoot } from 'react-dom/client';
import Contact from 'computing-den-lib/dist/client/Contact.jsx';
import Navbar from 'computing-den-lib/dist/client/Navbar.jsx';
import * as I from 'computing-den-lib/dist/client/Icon.jsx';
import { useGoToHash } from 'computing-den-lib/dist/client/misc.jsx';
import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { InitDataFromServerForSession, SessionListing } from '../common/types.js';
import moment from 'moment';
import TimeFromNow from './TimeFromNow.jsx';
import SessionsGrid from './SessionGrid.jsx';
import { getHashTags } from '../common/lib.js';

function setup() {
  const initDataDom = document.getElementById('init-data-from-server')!;
  const initData = JSON.parse(_.unescape(initDataDom.innerHTML));
  initDataDom.parentNode!.removeChild(initDataDom);

  const root = createRoot(document.getElementById('app')!);
  root.render(<App initData={initData} />);
}

window.onload = setup;

function App(props: { initData: InitDataFromServerForSession }) {
  useGoToHash(window.location.hash);

  return (
    <>
      <Navbar
        transparentAtTop
        links={[
          { label: 'INSTALL', href: 'https://marketplace.visualstudio.com/items?itemName=ComputingDen.codemic' },
          { label: 'Sessions', href: '/#sessions-section', primary: false },
          { label: 'GitHub', href: 'https://github.com/computing-den/CodeMic', primary: false },
          { label: 'Contact', href: '/#contact', primary: true },
        ]}
        breadcrumb={{ label: 'CodeMic', href: '/' }}
      />
      <PlayerSection session={props.initData.session} />
      <DescSection session={props.initData.session} />
      <SessionsSection sessions={props.initData.relatedSessions} />
      <Contact />
    </>
  );
}

type PlayerSrc = 'vscode' | 'youtube';

function PlayerSection(props: { session: SessionListing }) {
  const { head } = props.session;
  const [src, setSrc_] = useState<PlayerSrc>(() => {
    return ((head.youtubeId && localStorage.getItem('playerSrc')) || 'vscode') as PlayerSrc;
  });

  function setSrc(newSrc: PlayerSrc) {
    setSrc_(newSrc);
    localStorage.setItem('playerSrc', newSrc);
  }

  return (
    <section id="player-section">
      <div className="content">
        <div className="player">
          {src === 'youtube' && head.youtubeId && (
            <div className="iframe-container">
              <iframe
                className="ytplayer"
                src={`https://www.youtube.com/embed/${head.youtubeId}`}
                frameBorder="0"
                allowFullScreen
              />
            </div>
          )}
          {src === 'vscode' && (
            <a href={`vscode://computingden.codemic/s/${head.author}/${head.handle}`} className="cover-container">
              <img className="cover" src={`/session-cover?id=${head.id}`} />
              <I.IconPlay />
            </a>
          )}
        </div>
        <div className="avatar-container">
          <img className="avatar" src={`/avatar?username=${head.author}`} />
        </div>
        <div className="handle">
          {head.author}
          <span className="no-select"> </span>/<span className="no-select"> </span>
          {head.handle}
        </div>
        <h1 className="title">{head.title || 'Untitled'}</h1>
        <div className="actions">
          <Likes likes={props.session.publication.likes} />
          <SrcSwitch src={src} youtubeId={head.youtubeId} onChange={setSrc} />
        </div>
      </div>
    </section>
  );
}

function Likes(props: { likes: number }) {
  return (
    <div className="likes">
      <I.IconHeart /> {props.likes}
    </div>
  );
}

function SrcSwitch(props: { src: PlayerSrc; youtubeId?: string; onChange: (src: PlayerSrc) => void }) {
  return (
    <div className="src-switch">
      <div
        className={`src-switch-item ${props.src === 'vscode' ? 'active' : ''}`}
        tabIndex={0}
        onClick={() => props.onChange('vscode')}
        title="Watch in VSCode CodeMic"
      >
        <I.IconVSCode /> VSCode
      </div>
      <div
        className={`src-switch-item ${props.src === 'youtube' ? 'active' : ''} ${props.youtubeId ? '' : 'disabled'}`}
        tabIndex={0}
        onClick={() => props.youtubeId && props.onChange('youtube')}
        title={props.youtubeId ? 'Watch on YouTube' : 'YouTube recording not available'}
      >
        <I.IconYouTube /> YouTube
      </div>
    </div>
  );
}

const EXPAND_THRESHOLD = 1000;

function DescSection(props: { session: SessionListing }) {
  const { head, publication } = props.session;

  if (process.env.NODE_ENV === 'development') head.description = EXAMPLE_TEXT;

  const [expanded, setExpanded] = useState(false);
  const expandable = head.description.length > EXPAND_THRESHOLD;
  const description =
    expandable && !expanded ? head.description.substring(0, EXPAND_THRESHOLD) + '...' : head.description;
  const tags = getHashTags(head);
  const tagsStr = _.truncate(_.take(tags, 6).join(' '), { length: 60 });

  function toggleExpansion(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
  }

  return (
    <section id="desc-section">
      <div className="content">
        <div className="header">
          <span className="stats">{publication.views} views</span>
          <span> • </span>
          <TimeFromNow timestamp={publication.publishTimestamp} />
        </div>
        <div className="desc">
          <p className="tags">{tagsStr}</p>
          <TextToParagraphs text={description} />

          {expandable && (
            <a className="expand styled" href="#" onClick={toggleExpansion}>
              {expanded ? 'show less' : 'show more'}
            </a>
          )}
        </div>
      </div>
    </section>
  );
}

function SessionsSection(props: { sessions: SessionListing[] }) {
  return (
    <section id="sessions-section">
      <div className="content">
        <h3 className="section-subheading">EXPLORE & EXPERIMENT</h3>
        <h1 className="section-heading">Featured Sessions</h1>
        <SessionsGrid sessions={props.sessions} />
      </div>
    </section>
  );
}

function TextToParagraphs(props: { text: string }) {
  return (
    <>
      {props.text
        .split('\n')
        .map(x => x.trim())
        .filter(Boolean)
        .map(x => (
          <p>{x}</p>
        ))}
    </>
  );
}

const EXAMPLE_TEXT = `Billionaire investor and PayPal co-founder Peter Thiel sits down with Jordan Peterson for a powerful conversation about why real progress has stalled. Thiel argues that the last truly groundbreaking achievement may have been landing on the moon—and since then, we've slowed down. He explains how fear, red tape, and over-specialization have made us more cautious and less ambitious.

They dig into how society has shifted away from building and inventing, toward digital distractions and endless talk. They also explore what’s been lost as faith and meaning have disappeared from public life. From broken universities to status-driven culture wars, this is a deep and thought-provoking look at the challenges facing the West—and what we might do to turn things around.

Peter Thiel is a German-born entrepreneur, venture capitalist, activist, and billionaire who emigrated to the U.S. as a child, eventually settling in California after years of moving between countries. A Stanford Law graduate, he began his career as a clerk and derivatives trader before founding Thiel Capital with $1 million from friends and family. Despite early setbacks, he co-founded Confinity, which became PayPal, launching a streak of ventures including Palantir, Clarium Capital, and early investment in Facebook. Thiel is an openly gay supporter of the Republican party, advocating for both equal rights and certain conservative policies, making his political stance admirably nuanced.

This episode was filmed on March 31st, 2025.

Unlock the ad-free experience of The Jordan B. Peterson Podcast and dive into exclusive bonus content on DailyWire+. Start watching now: http://dwpluspeterson.com/yt

ALL LINKS: https://feedlink.io/jordanbpeterson


| Sponsors |

ExpressVPN: Go to https://expressvpn.com/jordan and find out how you can get 4 months of ExpressVPN free!

Hallow: Get 3 months free at https://hallow.com/Jordan

Join Peterson Academy: https://petersonacademy.com/   


| Links |

For Peter Thiel:

X https://x.com/peterthiel?lang=en 

Read “Zero to One: Notes on Startups, or How to Build the Future” https://a.co/d/fAfeXm8 


| Chapters |

(0:00) Coming up
(0:41) Intro
(2:51) We are stagnating, “the most innovative times are behind us.”
(8:01) Late modernity: hyper-specialization, reduced speed of travel, free thought becomes taboo
(14:51) How the U.S.—built on ambition and will—became risk adverse
(19:58) Why it feels like we’re living in an “apocalyptic age”
(23:30) Red tape strangles science, the Christian God always gets blamed
(31:44) Christian Europe ushered forth the scientific age, René Girard
(52:00) Pagans, power, imitation, and meta-games
(59:43) Thiel pushes back on the centering of sacrifice
(1:12:36) Abraham and Isaac: interpreting the Old Testament through the New Testament



// LINKS //

Peterson Academy https://petersonacademy.com
ARC https://www.arcforum.com

Books - https://www.jordanbpeterson.com/books/

#JordanPeterson #JordanBPeterson #DrJordanPeterson #DrJordanBPeterson #DailyWirePlus`;
